import { markApiStart, markApiEnd } from './tabPerf';
import { API_BASE } from './apiBase';

const _currentPath = () =>
  typeof window !== 'undefined' && window.location ? window.location.pathname : '';

// Default network timeout for any single call. Prevents the spinner
// from hanging indefinitely when an intermediate proxy holds a
// connection open. Callers may override with options.timeoutMs.
const DEFAULT_TIMEOUT_MS = 30000;

// Renew the access token the same way the axios client does (refresh token
// replayed in the body for native apps where the cookie isn't available).
// Returns true if a fresh token was stored. Shared so every authFetch call
// self-heals on a 401 instead of surfacing a red error / forcing a re-login.
async function _tryRefreshToken() {
  try {
    const resp = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ refreshToken: localStorage.getItem('refreshToken') }),
    });
    if (!resp.ok) throw new Error('refresh failed');
    const data = await resp.json();
    if (data?.token) {
      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      return true;
    }
    return false;
  } catch (_) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    return false;
  }
}

export default async function authFetch(url, options = {}, _retried = false) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const path = _currentPath();
  markApiStart(path);
  let response;
  try {
    response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
      signal: options.signal || controller.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      const timeoutErr = new Error(`Timeout après ${Math.round(timeoutMs / 1000)}s. Réseau lent — merci de réessayer.`);
      timeoutErr.status = 0;
      timeoutErr.detail = 'timeout';
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timer);
    markApiEnd(path);
  }

  // Access token expired → renew once and retry, so a stale 24h token doesn't
  // surface as a red error (no re-login needed). Same mechanism as api.js.
  if (response.status === 401 && !_retried) {
    const renewed = await _tryRefreshToken();
    if (renewed) {
      return authFetch(url, options, true);
    }
  }

  if (!response.ok) {
    // Try to surface FastAPI's `detail` so callers can show the actual
    // reason ("Créneau invalide", "Session expirée…") instead of a
    // generic "Erreur". Pydantic 422 detail is an array of issues —
    // we flatten the first one to a one-line French-ish message.
    let detail = null;
    try {
      const body = await response.json();
      if (typeof body?.detail === 'string') {
        detail = body.detail;
      } else if (Array.isArray(body?.detail) && body.detail.length > 0) {
        const first = body.detail[0];
        const loc = Array.isArray(first?.loc) ? first.loc.slice(-1)[0] : '';
        const msg = first?.msg || 'Valeur invalide';
        detail = loc ? `${loc}: ${msg}` : msg;
      }
    } catch (_) { /* non-JSON or empty body — ignore */ }

    const error = new Error(`HTTP ${response.status}`);
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return response.json();
}
